/**
 * Appointment-specific utility functions for formatting and normalization.
 */

import { DateTime } from "luxon";
import { getCompanyRegistry } from "../company/companyRegistrySingleton";
import { ensureUtcIso, fmtSalonHM } from "@/lib/time";
import { resolveMediaUrl } from "@/lib/resolveMediaUrl";
import { linesOwingMoney, type AbonementUse } from "@/lib/appointment/abonements";

/** Type for the react-i18next t function */
export type TFunction = (key: string, fallback: string) => string;

export function formatCreatedAt(dt?: string | null): string {
  if (!dt) return "—";
  const tz = getCompanyRegistry().getTimezone?.() ?? "UTC";
  // Normalize any ISO (with/without offset) → UTC, then to salon TZ
  const d = DateTime.fromISO(ensureUtcIso(dt, tz), { zone: "utc" }).setZone(tz);
  if (!d.isValid) return "—";
  return d.setLocale("ru").toFormat("d LLLL, HH:mm");
}

/**
 * Get visit status label (i18n-aware version).
 * Use this from components by passing the `t` function from useTranslation().
 */
export function getVisitStatusI18n(r: any, t: TFunction): string {
  if (r.attendance_status_slug === "waiting") return t("appointment.visit.waiting", "Waiting");
  if (r.attendance_status_slug === "visited" && r.is_paid) return t("appointment.visit.paid", "Visit paid");
  if (r.attendance_status_slug === "visited" && !r.is_paid) return t("appointment.visit.not_fully_paid", "Visit not fully paid");
  if (r.attendance_status_slug === "no_show") return t("appointment.visit.no_show", "No show");
  if (r.attendance_status_slug === "cancelled") return t("appointment.visit.cancelled", "Cancelled");
  return "—";
}

/**
 * Get reminder status label (i18n-aware version).
 */
export function getReminderStatusI18n(r: any, t: TFunction): string {
  if (r.is_remind_sms_sent || r.sms_now || r.sms_before || r.email_now || r.is_client_notification_sent) {
    return t("appointment.reminder.sent", "Sent");
  }
  return t("appointment.reminder.not_sent", "Not sent");
}

/**
 * Get review status label (i18n-aware version).
 */
export function getReviewStatusI18n(r: any, t: TFunction): string {
  return r.review_requested
    ? t("appointment.review.requested", "Requested")
    : t("appointment.review.not_requested", "Not requested");
}

/**
 * Get sale bill printed label (i18n-aware version).
 */
export function getSaleBillPrintedI18n(r: any, t: TFunction): string {
  return r.is_sale_bill_printed
    ? t("common.yes", "Yes")
    : t("common.no", "No");
}

/**
 * Get prepaid label (i18n-aware version).
 */
export function getPrepaidI18n(r: any, t: TFunction): string {
  return r.prepaid
    ? t("common.exists", "Exists")
    : t("common.no", "No");
}

export function formatClientTags(raw: any): any[] {
  return Array.isArray(raw.client?.client_tags)
    ? raw.client.client_tags.map((tag: any) => ({
      id: tag.id,
      title: tag.title,
      color: tag.color,
      font_color: tag.font_color,
      icon: tag.icon,
    }))
    : [];
}

export function formatDateString(raw: any): string {
  const tz = getCompanyRegistry().getTimezone?.() ?? "UTC";
  const src = raw?.datetime ?? raw?.date;
  if (!src) return "—";
  // If datetime is present, respect salon day boundaries
  const d = raw?.datetime
    ? DateTime.fromISO(ensureUtcIso(String(raw.datetime), tz), { zone: "utc" }).setZone(tz)
    : DateTime.fromISO(String(raw.date), { zone: tz });
  return d.isValid ? d.setLocale("ru").toFormat("dd.LL.yyyy") : "—";
}

export function formatStartTimeString(raw: any): string { return getStartTime(raw); }

export function formatEndTimeString(raw: any): string { return getEndTime(raw); }

export function formatServicePriceString(raw: any): string {
  if (!Array.isArray(raw.services) || raw.services.length === 0) return "—";
  const prices = raw.services.map(
    (s: any) =>
      typeof s.cost_to_pay === "number" && typeof s.amount === "number"
        ? s.cost_to_pay * s.amount
        : undefined
  ).filter((v: any) => typeof v === "number");
  if (prices.length === 0) return "—";
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return formatCurrency(min);
  return `${formatCurrency(min)} – ${formatCurrency(max)}`;
}

export function formatServiceDurationString(raw: any): string {
  const durationSec = raw.length !== undefined ? Number(raw.length) : raw.duration;
  if (typeof durationSec !== "number" || isNaN(durationSec)) return "—";
  const hours = Math.floor(durationSec / 3600);
  const minutes = Math.floor((durationSec % 3600) / 60);
  const durationStr =
    hours > 0
      ? minutes > 0
        ? `${hours}h ${minutes}min`
        : `${hours}h`
      : minutes > 0
        ? `${minutes}min`
        : "0min";
  // Use startTime and endTime if available
  const pad = (n: number) => n.toString().padStart(2, "0");
  let start = "";
  let end = "";
  if (raw.datetime) {
    const startDate = DateTime.fromISO(raw.datetime);
    if (startDate.isValid) {
      start = `${pad(startDate.hour)}:${pad(startDate.minute)}`;
      if (Number.isFinite(durationSec)) {
        const endDate = startDate.plus({ seconds: durationSec });
        end = `${pad(endDate.hour)}:${pad(endDate.minute)}`;
      }
    }
  }
  if (start && end) {
    return `${durationStr} (${start} – ${end})`;
  }
  return durationStr;
}

export function getDurationHours(raw: any): number {
  const durationSec = raw.duration;
  if (typeof durationSec !== "number" || isNaN(durationSec)) return 1;
  let hours = Math.floor(durationSec / 3600);
  if (hours < 1) hours = 1;
  if (hours > 12) hours = 12;
  return hours;
}

export function getDurationMinutes(raw: any): number {
  const durationSec = raw.duration;
  if (typeof durationSec !== "number" || isNaN(durationSec)) return 0;
  let minutes = Math.floor((durationSec % 3600) / 60);
  // Round to nearest 5
  minutes = Math.round(minutes / 5) * 5;
  if (minutes < 0) minutes = 0;
  if (minutes > 55) minutes = 55;
  return minutes;
}

// --- Additional appointment composition utilities ---

export function getEndTimeISO(raw: any): string {
  const tz = getCompanyRegistry().getTimezone?.() ?? "UTC";
  if (raw?.datetime && Number.isFinite(raw?.length)) {
    const startUtc = DateTime.fromISO(ensureUtcIso(String(raw.datetime), tz), { zone: "utc" });
    return startUtc.plus({ seconds: raw.length }).toUTC().toISO({ suppressMilliseconds: true });
  }
  return "";
}

export function composeClientObj(raw: any): {
  id?: number | string;
  name: string;
  phone: string;
  email?: string;
  additional_phone?: string;
  birthday?: string;
  comment?: string;
  card_number?: string;
  gender?: number;
  discount?: number;
  confirm_method?: number;
  messenger?: string | null;
  messenger_label?: string | null;
  is_blocked?: boolean;
} | undefined {
  if (!raw.client) return undefined;
  return {
    id: raw.client.id,
    name: raw.client.name,
    phone: raw.client.phone,
    email: raw.client.email || "",
    additional_phone: raw.client.additional_phone || "",
    birthday: raw.client.birthday || "",
    comment: raw.client.comment || "",
    card_number: raw.client.card_number || raw.client.card || "",
    gender: typeof raw.client.gender === "number" ? raw.client.gender : (raw.client.sex ?? 0),
    discount: typeof raw.client.discount === "number" ? raw.client.discount : 0,
    confirm_method: typeof raw.client.confirm_method === "number" ? raw.client.confirm_method : undefined,
    messenger: typeof raw.client.messenger === "string" ? raw.client.messenger : undefined,
    messenger_label: typeof raw.client.messenger_label === "string" ? raw.client.messenger_label : undefined,
    is_blocked: raw.client.is_blocked === true,
  };
}

export function composeStaffObj(raw: any): { id: any; name: string; specialization: any; avatar: string } | undefined {
  return raw.staff
    ? {
      id: raw.staff.id,
      name: raw.staff.name,
      specialization: raw.staff.specialization,
      avatar: resolveMediaUrl(raw.staff.avatar || raw.staff.image) || "",
    }
    : undefined;
}

export function composeServicesArr(raw: any) {
  const normalizeLength = (v: any) => {
    if (v === null || typeof v === "undefined") return null;
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    if (typeof v === "string") {
      const num = Number(v);
      if (Number.isFinite(num)) return num;
      // support HH:MM or HH:MM:SS formats
      const parts = v.split(":").map(Number);
      if (parts.every((n) => Number.isFinite(n))) {
        if (parts.length === 2) return parts[0] * 3600 + parts[1] * 60;
        if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
      }
    }
    return null;
  };
  return Array.isArray(raw.services)
    ? raw.services.map((s: any) => ({
      id: s.id,
      title: s.title,
      category_id: s.category_id,
      cost_to_pay: s.cost_to_pay,
      cost: s.cost,          // added
      first_cost: s.first_cost,    // added
      manual_cost: s.manual_cost,
      cost_per_unit: s.cost_per_unit, // added
      discount: s.discount,
      // provenance of an auto-applied discount (last-hour fill badge)
      discount_source: s.discount_source ?? null,
      amount: s.amount,
      quantity: s.quantity,
      // preserve length/duration for time recalculations
      length: normalizeLength(s.length) ?? normalizeLength(s.duration) ?? 0,
    }))
    : [];
}

import { formatCurrency } from "../utils";

/**
 * Original last-hour offer percent for a service line, derived from the
 * IMMUTABLE audit pair (first_cost = discounted price at creation,
 * cost = list price) — so the badge keeps showing the original offer even
 * after the admin resets the discount or sets a manual price.
 * Returns null when the line did not come from a last-hour offer.
 */
export function lastHourOfferPct(s: any): number | null {
  if (s?.discount_source !== "last_hour_fill") return null;
  const cost = Number(s?.cost);
  const fc = Number(s?.first_cost);
  if (!Number.isFinite(cost) || cost <= 0 || !Number.isFinite(fc)) return 0;
  const pct = Math.round((1 - fc / cost) * 100);
  return pct > 0 ? pct : 0;
}

// --- Shared price calculation helpers ---

/**
 * Clamp a percentage value to [0, 100].
 */
function safePct(n: any): number {
  const v = Number(n);
  if (isNaN(v) || v < 0) return 0;
  if (v > 100) return 100;
  return v;
}

/**
 * Round to 2 decimal places.
 */
function round2(n: number): number {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * Get the base unit price for a service (before discount) for display purposes.
 * For paid appointments where manual_cost is set, returns first_cost as the original/base price.
 * Priority: first_cost (if manual_cost is set) → manual_cost → cost_per_unit → cost → first_cost → reverse from cost_to_pay
 * 
 * @deprecated Use `getServiceUnitPrice` from `@/lib/appointment/costEngine` instead.
 */
export function getServiceUnitPrice(service: any): number {
  // If manual_cost is set, it means this is a paid/finalized service.
  // In this case, show first_cost as the original price (for "Цена, BYN" column)
  const hasManualCost = service.manual_cost != null && service.manual_cost !== "" && Number(service.manual_cost) !== 0;
  if (hasManualCost && service.first_cost != null && service.first_cost !== "") {
    return Number(service.first_cost) || 0;
  }

  // If no manual_cost, use regular priority
  if (hasManualCost) {
    return Number(service.manual_cost) || 0;
  }

  // Fallback to unit/cost fields
  if (service.cost_per_unit != null && service.cost_per_unit !== "") return Number(service.cost_per_unit) || 0;
  if (service.cost != null && service.cost !== "") return Number(service.cost) || 0;
  if (service.first_cost != null && service.first_cost !== "") return Number(service.first_cost) || 0;

  // If we have a cost_to_pay (after discount) and a discount, we can reverse-engineer the base price
  const qty = Math.max(1, Number(service.amount ?? service.quantity ?? 1));
  const disc = safePct(service.discount);
  if (service.cost_to_pay != null && service.cost_to_pay !== "") {
    const line = Number(service.cost_to_pay) || 0; // line total (after discount)
    const baseTotal = disc === 100 ? 0 : (line / (1 - disc / 100));
    return qty > 0 ? Math.round((baseTotal / qty) * 100) / 100 : 0;
  }
  return 0;
}

/**
 * Get the line total for a service (the final amount to pay/paid).
 * 
 * Logic:
 * - If manual_cost is set AND differs from first_cost: it's the actual paid amount,
 *   return manual_cost × quantity directly (discount already applied).
 * - If manual_cost is set AND equals first_cost: it's an anchor price for editing,
 *   apply discount to calculate the total.
 * - Otherwise use cost_to_pay if available, or calculate from unit price with discount.
 * 
 * @deprecated Use `getServiceLineTotal` from `@/lib/appointment/costEngine` instead.
 */
export function getServiceLineTotal(service: any): number {
  const qty = Math.max(1, Number(service.amount ?? service.quantity ?? 1));
  const disc = safePct(service.discount);

  const hasManualCost = service.manual_cost != null && service.manual_cost !== "" && Number(service.manual_cost) !== 0;
  const manualCostVal = hasManualCost ? Number(service.manual_cost) : 0;
  const firstCostVal = service.first_cost != null && service.first_cost !== "" ? Number(service.first_cost) : 0;

  if (hasManualCost) {
    // Check if manual_cost differs from first_cost (paid appointment with custom price)
    const isFinalPaidAmount = firstCostVal > 0 && Math.abs(manualCostVal - firstCostVal) > 0.01;

    if (isFinalPaidAmount) {
      // manual_cost IS the final paid amount per unit - don't re-apply discount
      return round2(manualCostVal * qty);
    } else {
      // manual_cost equals first_cost - it's an anchor price, apply discount
      return round2(manualCostVal * qty * (1 - disc / 100));
    }
  }

  // Fallback: use cost_to_pay if available (the persisted calculated total)
  if (service.cost_to_pay != null && service.cost_to_pay !== "") {
    return Number(service.cost_to_pay);
  }

  // Final fallback: Re-calculate from stored fields with discount
  const unit = getServiceUnitPrice(service);
  return round2(unit * qty * (1 - disc / 100));
}

/**
 * Normalizes the kkmState for an appointment.
 * Returns the normalized kkmState object or null if not present.
 */
export function normalizeKkmState(raw: any): {
  showPayerInfo: boolean;
  showPositionsInfo: boolean;
  lastOperationType: string | null;
  receiptPdfLink: string;
  transactions: any[];
} | null {
  if (!raw.kkm_state) return null;
  return {
    showPayerInfo: !!raw.kkm_state.show_payer_info,
    showPositionsInfo: !!raw.kkm_state.show_positions_info,
    lastOperationType: raw.kkm_state.last_operation_type ?? null,
    receiptPdfLink: raw.kkm_state.receipt_pdf_link || "",
    transactions: Array.isArray(raw.kkm_state.transactions) ? raw.kkm_state.transactions : [],
  };
}

/**
 * Normalizes the paymentMethods for an appointment.
 * Returns an array of normalized payment method objects.
 */
export function normalizePaymentMethods(raw: any): {
  slug: string;
  isApplicable: boolean;
  applicableAmount: number;
  account: {
    id: any;
    title: string;
    isCash: boolean;
    isDefault: boolean;
    paid_amount: number;
  } | null;
}[] {
  if (!Array.isArray(raw.payment_methods)) return [];
  return raw.payment_methods.map((m: any) => ({
    slug: m.slug || "",
    isApplicable: !!m.is_applicable,
    applicableAmount: typeof m.applicable_amount === "number" ? m.applicable_amount : 0,
    account: m.account
      ? {
        id: m.account.id,
        title: m.account.title,
        isCash: !!m.account.is_cash,
        isDefault: !!m.account.is_default,
        paid_amount:
          typeof m.account.paid_amount === "number" ? Number(m.account.paid_amount) : 0,
      }
      : null,
  }));
}

/**
 * Normalizes the payment_summary for an appointment.
 * Returns an array of payment summary items or null if not present.
 * This is used to display payment breakdown when appointment is paid off.
 */
export function normalizePaymentSummary(raw: any): {
  accountId: number;
  accountTitle: string;
  isCash: boolean;
  totalPaid: number;
}[] | null {
  if (!Array.isArray(raw.payment_summary) || raw.payment_summary.length === 0) {
    return null;
  }
  return raw.payment_summary.map((item: any) => ({
    accountId: item.account_id,
    accountTitle: item.account_title || "",
    isCash: !!item.is_cash,
    totalPaid: typeof item.total_paid === "number" ? item.total_paid : 0,
  }));
}

/**
 * Returns payment status label based on totalPaid and totalAmount (i18n-aware version).
 */
export function getPaymentStatusLabelByAmountI18n(totalPaid: number, totalAmount: number, t: TFunction): string {
  if (totalAmount > 0 && totalPaid === totalAmount) return t("appointment.payment.fully_paid", "Fully paid");
  if (totalPaid > 0 && totalPaid < totalAmount) return t("appointment.payment.partially_paid", "Partially paid");
  return t("appointment.payment.not_paid", "Not paid");
}

/**
 * Calculates payment section fields for an appointment, including paid_full logic.
 * Returns: { totalAmount, totalPaid, totalRemaining, totalAmountString, totalPaidString, totalRemainingString }
 */
export function calculatePaymentSection(raw: any, servicesArr: any[], abonementUses?: AbonementUse[] | null) {
  // Use the shared getServiceLineTotal helper for each service to ensure
  // the total matches the sum of individual line totals displayed in the UI.
  // Lines a client package pays owe no money (abonements.ts).
  const owing = linesOwingMoney(servicesArr, abonementUses ?? raw?.paymentSection?.abonementUses);
  const totalAmount = owing.reduce(
    (sum, s) => sum + getServiceLineTotal(s),
    0
  );

  let totalPaid = 0;
  const paidFromMethods = Array.isArray(raw?.paymentSection?.paymentMethods)
    ? raw.paymentSection.paymentMethods.reduce(
      (sum: number, m: any) => sum + (Number(m?.account?.paid_amount) || 0),
      0
    )
    : 0;

  if (paidFromMethods > 0) {
    totalPaid = paidFromMethods;
  } else if (raw?.paymentSection?.paidFull === 1 || raw?.paid_full === 1) {
    totalPaid = totalAmount;
  }

  const totalRemaining = Math.max(0, totalAmount - totalPaid);

  return {
    totalAmount,
    totalPaid,
    totalRemaining,
    totalAmountString: formatCurrency(totalAmount),
    totalPaidString: formatCurrency(totalPaid),
    totalRemainingString: formatCurrency(totalRemaining),
  };
}

export function determineColor(raw: any, servicesArr: any[]): string {
  if (raw.custom_color) return raw.custom_color;
  if (servicesArr.length > 0 && servicesArr[0].category_id) {
    switch (servicesArr[0].category_id) {
      case 1104759:
        return "bg-blue-100";
      case 1104827:
        return "bg-orange-100";
      case 1167599:
        return "bg-green-200";
      default:
        return "bg-gray-200";
    }
  }
  return "bg-gray-200";
}

export function composeTitle(raw: any, servicesArr: any[]): string {
  // Derive a display title from *all* services, e.g.:
  //   "Service A\n- Service B\n- Service C"
  const titles = Array.isArray(servicesArr)
    ? servicesArr
      .map((s: any) =>
        s && typeof s.title === "string" ? s.title.trim() : ""
      )
      .filter((t: string) => t.length > 0)
    : [];

  if (titles.length > 0) {
    return titles
      .map((t, idx) => (idx === 0 ? t : `- \n${t}`))
      .join("\n");
  }

  // Fallback to backend-provided title when no services are present.
  return typeof raw?.title === "string" ? raw.title : "";
}

export function getStartTime(raw: any): string {
  const dt = raw.datetime;
  if (!dt) return "";
  const d = DateTime.fromISO(dt);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return d.isValid ? `${pad(d.hour)}:${pad(d.minute)}` : "";
}

export function getEndTime(raw: any): string {
  if (raw.datetime && raw.length) {
    const start = DateTime.fromISO(raw.datetime);
    if (!start.isValid) return "";
    const end = start.plus({ seconds: Number(raw.length) });
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${pad(end.hour)}:${pad(end.minute)}`;
  }
  return "";
}

/**
 * Normalizes the visit history section for an appointment.
 * Returns an array of normalized visit history entries.
 */
export function normalizeVisitHistorySection(raw: any): any[] {
  if (!Array.isArray(raw.visit_history)) return [];
  const tz = getCompanyRegistry().getTimezone?.() ?? "UTC";
  return raw.visit_history.map((entry: any) => {
    const src = String(entry?.date ?? "");
    if (!src) return { ...entry, date: "", time: "" };
    const isoUtc = ensureUtcIso(src.includes("T") ? src : src.replace(" ", "T"), tz);
    const dSalon = DateTime.fromISO(isoUtc, { zone: "utc" }).setZone(tz);
    // Raw attendance slug for i18n translation in components
    // Components should use i18n to translate: confirmed, visited, no_show
    let status: "confirmed" | "visited" | "no_show" = "confirmed";
    if (entry.attendance === 1) status = "visited";
    else if (entry.attendance === -1 || entry.attendance === 0) status = "no_show";

    // Employee
    let employee = {
      name: entry.staff?.name || "",
      photo: resolveMediaUrl(entry.staff?.avatar) || undefined,
    };

    // Services
    let services =
      Array.isArray(entry.services) && entry.services.length > 0
        ? entry.services.map((s: any) => ({
          name: s.title || "",
          cost: typeof s.cost_to_pay === "number" ? s.cost_to_pay : s.first_cost || 0,
          paid: typeof s.paid_sum === "number" ? s.paid_sum : s.payed_cost || 0,
        }))
        : [];

    // Additional items (consumables)
    let additionalItems: any[] = [];
    if (Array.isArray(entry.services)) {
      entry.services.forEach((s: any) => {
        if (Array.isArray(s.consumables) && s.consumables.length > 0) {
          s.consumables.forEach((c: any) => {
            additionalItems.push({
              name: c.title || "",
              quantity: Math.abs(c.amount) || 0,
              unit: c.unit || "",
              cost: typeof c.cost_per_unit === "number" ? c.cost_per_unit * Math.abs(c.amount) : 0,
            });
          });
        }
      });
    }
    if (additionalItems.length === 0) {
      additionalItems = [];
    }

    // Location
    let location = entry.company?.title || "";

    // Comment
    let comment = entry.comment || "";

    return {
      id: entry.id,
      date: dSalon.toISODate(),
      time: dSalon.toFormat("HH:mm"),
      employee,
      status,
      services,
      additionalItems,
      comment,
      location,
    };
  });
}

// --- Consumables normalization utilities ---

/**
 * Normalizes the storage array in consumables to a dictionary keyed by id.
 */
export function normalizeConsumablesStorage(consumables: any): Record<number, any> {
  const storageArr = Array.isArray(consumables?.storage) ? consumables.storage : [];
  const storageDict: Record<number, any> = {};
  storageArr.forEach((s: any) => {
    storageDict[s.id] = s;
  });
  return storageDict;
}

/**
 * Normalizes the technological_cards array in consumables.
 */
export function normalizeTechnologicalCardsList(consumables: any): any[] {
  return Array.isArray(consumables?.technological_cards)
    ? consumables.technological_cards
    : [];
}

/**
 * Builds a deduplicated goodsList from technologicalCardsList and storageDict.
 */
export function normalizeGoodsList(consumables: any, storageDict: Record<number, any>): {
  id: number;
  title: string;
  unit: string;
  cost: number;
  storage_id: number;
  storage_title: string;
}[] {
  const technologicalCardsList = normalizeTechnologicalCardsList(consumables);
  const goodsMap: Record<number, {
    id: number;
    title: string;
    unit: string;
    cost: number;
    storage_id: number;
    storage_title: string;
  }> = {};

  technologicalCardsList.forEach((card: any) => {
    (card.technological_card_items || []).forEach((item: any) => {
      const good = item.good;
      if (!goodsMap[good.id]) {
        const storage_id = item.storage_id;
        goodsMap[good.id] = {
          id: good.id,
          title: good.title,
          unit: good.unit,
          cost: item.price,
          storage_id,
          storage_title: storageDict[storage_id]?.title || "—",
        };
      }
    });
  });

  return Object.values(goodsMap);
}

// -----------------------------------------------------------------------------
// Appointment "card status" (single glyph) derivation
// -----------------------------------------------------------------------------
import { AppointmentStatus, type AppointmentStatus as TAppointmentStatus } from "./status";

/**
 * Derive a single status for timetable cards.
 * Uses commonly available fields on normalized records; falls back gracefully.
 *
 * Priority:
 * - attendance "no_show" → no_show
 * - attendance "visited" (+ paid?) → arrived_paid / arrived_unpaid
 * - confirmed flag → confirmed
 * - default → waiting
 */
export function deriveCardStatus(r: any): TAppointmentStatus {
  const attendance = r.attendance;
  const confirmed = r.confirmed === 1 || r?.confirmed === true;
  // payment signals come from either a boolean/flag or a computed section
  const paidFull =
    r?.is_paid === true ||
    r?.paymentSection?.paidFull === 1 ||
    r?.paidFull === 1;

  if (attendance === 0) {
    return confirmed ? AppointmentStatus.CONFIRMED : AppointmentStatus.WAITING;
  } else {
    if (attendance === -1) return AppointmentStatus.NO_SHOW;
    if (attendance === 1) return paidFull
      ? AppointmentStatus.ARRIVED_PAID
      : AppointmentStatus.ARRIVED_UNPAID;
  }
  return AppointmentStatus.WAITING;
}

/**
 * Normalizes the recorded array in consumables.
 */
export function normalizeConsumablesRecorded(consumables: any): any[] {
  return Array.isArray(consumables?.recorded) ? consumables.recorded : [];
}
